import { all, call, put, select, spawn, take, takeLatest, takeLeading } from 'redux-saga/effects';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

import { LIMIT_DEFAULT } from '@/api/api.constant';
import { events$filterQuery, events$get$count, events$get$s, searchEvents } from '@/api/events';
import { selectEventsCards, selectEventsSearchIds } from '@/store/calendar-events/calendar-events.selectors';
import { EventCardS, EventCardT } from '@/types/calendar';
import { dates, storageKeys } from '@/utils/constants';
import { Cookies } from '@/utils/cookies';
import { dateAsNumber, priceAsNumber } from '@/utils/number-utils';

import { selectFiltersSelected, selectSearchArgs } from '../calendar-filters';
import { CalendarFiltersActionTypes } from '../calendar-filters/calendar-filters.actions';
import { selectRulerDateRange } from '../calendar-navigation';
import { CalendarNavigationActionTypes } from '../calendar-navigation/calendar-navigation.actions';
import {
    CalendarEventsActionTypes,
    clearEvents,
    eventLoadingStatusSet,
    eventsLoad,
    eventsLoadSuccess,
    eventsSearchLoadSuccess,
} from './calendar-events.actions';

dayjs.extend(utc);

function* eventsLoad$() {
    yield put(eventLoadingStatusSet(true));
    const searchArgs: ReturnType<typeof selectSearchArgs> = yield select(selectSearchArgs);
    const rulerDateRange: ReturnType<typeof selectRulerDateRange> = yield select(selectRulerDateRange);
    if (!rulerDateRange[0] || !rulerDateRange[1]) return;

    const searchDateStart = searchArgs.ranged ? rulerDateRange[0] : dates.today.format(dates.format);
    const searchDateEnd = searchArgs.ranged ? rulerDateRange[1] : dates.today.add(3, 'month').format(dates.format);

    if (searchArgs.searchString) {
        const eventsSearched: Array<{ event_id: string; score: number }> = yield call(searchEvents, {
            search_text: searchArgs.searchString,
            start_date: searchDateStart,
            end_date: searchDateEnd,
        });
        if (eventsSearched.length === 0) {
            yield put(clearEvents());
            yield put(eventLoadingStatusSet(false));
            return;
        }
        yield put(eventsSearchLoadSuccess(eventsSearched));
    }

    const selectedSearchIds: ReturnType<typeof selectEventsSearchIds> = yield select(selectEventsSearchIds);
    const filtersSelected: ReturnType<typeof selectFiltersSelected> = yield select(selectFiltersSelected);

    const filterQuery = events$filterQuery({
        locationCity: filtersSelected.city.map((item) => item.value),
        typeTag: filtersSelected.event_type.map((item) => item.value),
        typeLanguage: filtersSelected.language.map((item) => item.value),
        typePrice: filtersSelected.price.map((item) => item.value),
        countryCode: filtersSelected.countryCode,
        dateStart: selectedSearchIds.length > 0 ? searchDateStart : rulerDateRange[0],
        dateEnd: selectedSearchIds.length > 0 ? searchDateEnd : rulerDateRange[1],
        lang: Cookies.get(storageKeys.prefLanguage) ?? 'en',
        eventIds: selectedSearchIds.map((item) => item.event_id),
    });
    const eventsCount: number = yield call(events$get$count, filterQuery);
    const selectedEvents: EventCardT[] = yield select(selectEventsCards);
    if (eventsCount === selectedEvents.length) {
        return;
    }
    yield put(clearEvents());
    let offset = 0;
    const eventCardsPrepared: EventCardT[] = [];
    while (offset < eventsCount) {
        try {
            const eventCards: EventCardS[] = yield call(events$get$s, filterQuery, LIMIT_DEFAULT, offset);
            eventCardsPrepared.push(...transformEventCards(eventCards, selectedSearchIds));
        } catch {}
        offset += LIMIT_DEFAULT;
    }
    yield put(eventsLoadSuccess(eventCardsPrepared));

    take(CalendarEventsActionTypes.eventsLoadSuccess);
    yield put(eventLoadingStatusSet(false));
}

function* eventsLoadSaga() {
    yield takeLeading(CalendarEventsActionTypes.eventsLoad, eventsLoad$);
}

function* searchEventsTrigger() {
    const eventIds: string[] = yield select(selectEventsSearchIds) ?? [];
    if (eventIds.length === 0) return;
    yield put(eventsLoad());
}

function* eventCardsLoadTriggerSaga() {
    yield all([
        takeLatest(CalendarFiltersActionTypes.filtersLoadSuccess, eventsLoad$),
        takeLatest(CalendarFiltersActionTypes.filterSelect, eventsLoad$),
        takeLatest(CalendarFiltersActionTypes.filterDeselect, eventsLoad$),
        takeLatest(CalendarFiltersActionTypes.filtersReset, eventsLoad$),
        takeLatest(CalendarEventsActionTypes.eventsSearchReset, eventsLoad$),
        takeLatest(CalendarFiltersActionTypes.searchSortByScoreSet, searchEventsTrigger),
        takeLatest(CalendarNavigationActionTypes.rulerDateRange, eventsLoad$),
        takeLatest(CalendarFiltersActionTypes.searchRangeSet, eventsLoad$),
    ]);
}

export function* calendarEventsEffects() {
    yield spawn(eventsLoadSaga);
    yield spawn(eventCardsLoadTriggerSaga);
}

function transformEventCards(
    eventCards: EventCardS[],
    searchedIds?: Array<{ event_id: string; score: number }>,
): EventCardT[] {
    for (const card of eventCards as EventCardT[]) {
        const score = searchedIds?.find((item) => item.event_id === card.id)?.score;
        card.name = card.translations[0]?.event_name ?? card.name;
        card.dateInt = dateAsNumber(dayjs(card.start_date));
        card.visitPrice = priceAsNumber(card.visit_price);
        score && (card.score = score);
    }
    return eventCards as EventCardT[];
}
